import * as React from 'react';
import Description from '@material-ui/icons/Description';
import Group from '@material-ui/icons/Group';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { DeepReadonly } from 'utility-types';
import { EsignatureContainer } from '../../containers/Nav/Esignature';
import { IncidentReportNav } from '../../containers/Nav/IncidentReportNav';
import { IOrganization, OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { Location } from 'history';
import { NavLinkListItem } from './NavLinkListItem';
import { ReportingSectionContainer } from '../../containers/Nav/ReportingSection';
import { SafetyProgramsContainer } from '../../containers/Nav/SafetyPrograms';
import { SubcontractorManagementContainer } from '../../containers/Nav/SubcontractorManagement';
import { TooltipBadge } from '../TooltipBadge';
import { TrainingSection } from './TrainingSection';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { VerisourceOQNavigation } from './VerisourceOQNavigation';

const { Read, Manage, Write } = ActivityAction;
const {
  ActivityDocuments,
  AdminQuestions,
  ContractorReviews,
  ContractorsQuestionnaire,
  Employees,
  Organizations,
  SafetyProgramAnswers,
  Subcontractors
} = ActivityResourceName;
const { Subcontractor, PrimeContractor, QuestionnaireV2, Documents } = OrganizationFeature;

const { REACT_APP_NAME } = process.env;
const { AionSubscriberUI, AionEmployeeUI, AionQuestionnaireUI, AionQuestionnaireDocumentsUI } = AppName;
const {
  Esignature,
  FlexTrack,
  IncidentReports,
  RegionMetrics,
  RegionMetricsWithoutTotal,
  OperationalMetrics,
  EmployeeLinkManagement,
  ContractorReviews: ContractorReviewsFeature,
  ChasOrganizationFields
} = OrganizationFeature;

export type Props = {
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasVerificationSubscription: boolean;
  organization: DeepReadonly<IOrganization>;
  navExpanded: boolean;
  location: Location;
};

export const SubscriberLinks: React.FC<Props> = ({
  organization,
  navExpanded,
  hasGlobalPermission,
  hasOrganizationPermission,
  hasVerificationSubscription,
  location
}) => {
  const { clientFeatures, features } = organization;
  const { t } = useTranslation();
  const isAdmin = hasGlobalPermission(Manage, Organizations);
  const isVisibleForNonChasOrganization = isAdmin || !features.includes(ChasOrganizationFields);
  const isQuestionnaireDocumentsEnabled = features.includes(Documents);
  const isQuestionnaireV2Enabled = features.includes(QuestionnaireV2);
  const hasQuestionnaireLink =
    features.includes(QuestionnaireV2) &&
    (hasGlobalPermission(Read, ContractorsQuestionnaire) ||
      hasOrganizationPermission(Read, ContractorsQuestionnaire) ||
      hasGlobalPermission(Read, AdminQuestions) ||
      hasOrganizationPermission(Read, AdminQuestions));
  const hasDocumentsLink =
    isQuestionnaireDocumentsEnabled &&
    (hasGlobalPermission(Read, ActivityDocuments) || hasOrganizationPermission(Read, ActivityDocuments));
  const { answersToBePublishedCount } = useSelector((state: CommonRootState) => state.questionnaireStatus);

  const tooltipBadge = (
    <TooltipBadge
      badgeContent={answersToBePublishedCount}
      tooltipTitle={t('components.subscriberLinks.unpublishedAnswersBadgeCount', {
        defaultValue: '{{answersToBePublishedCount}} unpublished',
        answersToBePublishedCount
      })}
    />
  );

  return (
    <React.Fragment>
      {clientFeatures && clientFeatures.includes(Esignature) && (
        <EsignatureContainer organizationId={organization.id} location={location} />
      )}
      {features.includes(OrganizationFeature.VerisourceContractorOQNavigation) ? (
        <VerisourceOQNavigation organization={organization} client={false} />
      ) : null}
      {features.includes(EmployeeLinkManagement) &&
        hasOrganizationPermission(Write, Employees) &&
        (REACT_APP_NAME === AionEmployeeUI ? (
          <NavLinkListItem divider tooltip={!navExpanded} to={`/${organization.id}/employees`} icon={<Group />}>
            {t('components.subscriberLinks.employeeManagement', 'Employee Management')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem
            divider
            tooltip={!navExpanded}
            href={`/organizations/${organization.id}/employees`}
            icon={<Group />}
          >
            {t('components.subscriberLinks.employeeManagement', 'Employee Management')}
          </AnchorListItem>
        ))}
      <IncidentReportNav organizationId={organization.id} navExpanded={navExpanded} />
      {hasQuestionnaireLink && (
        <>
          {REACT_APP_NAME === AionQuestionnaireUI ? (
            <NavLinkListItem divider to={`/${organization.id}/questionnaire-v2`} icon={<ListAltIcon />}>
              {t('components.subscriberLinks.questionnaire', 'Questionnaire')}
              {tooltipBadge}
            </NavLinkListItem>
          ) : (
            <AnchorListItem divider href={`/organizations/${organization.id}/questionnaire-v2`} icon={<ListAltIcon />}>
              {t('components.subscriberLinks.questionnaire', 'Questionnaire')}
              {tooltipBadge}
            </AnchorListItem>
          )}
        </>
      )}
      {!isQuestionnaireV2Enabled &&
        (REACT_APP_NAME === AionSubscriberUI ? (
          <NavLinkListItem
            divider
            tooltip={!navExpanded}
            to={`/${organization.id}/questionnaire`}
            icon={<ListAltIcon />}
          >
            {t('components.subscriberLinks.questionnaire', 'Questionnaire')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem
            divider
            tooltip={!navExpanded}
            href={`/organizations/${organization.id}/questionnaire`}
            icon={<ListAltIcon />}
          >
            {t('components.subscriberLinks.questionnaire', 'Questionnaire')}
          </AnchorListItem>
        ))}
      {hasDocumentsLink && (
        <>
          {REACT_APP_NAME === AionQuestionnaireDocumentsUI ? (
            <NavLinkListItem divider to={`/${organization.id}/documents`} icon={<Description />}>
              {t('components.subscriberLinks.documents', 'Documents')}
            </NavLinkListItem>
          ) : (
            <AnchorListItem divider href={`/organizations/${organization.id}/documents`} icon={<Description />}>
              {t('components.subscriberLinks.documents', 'Documents')}
            </AnchorListItem>
          )}
        </>
      )}
      {(features.includes(FlexTrack) ||
        features.includes(IncidentReports) ||
        features.includes(RegionMetrics) ||
        features.includes(RegionMetricsWithoutTotal) ||
        features.includes(OperationalMetrics) ||
        (features.includes(ContractorReviewsFeature) &&
          (hasOrganizationPermission(Read, ContractorReviews) || hasGlobalPermission(Read, ContractorReviews)))) &&
        isVisibleForNonChasOrganization && (
          <ReportingSectionContainer organizationId={organization.id} location={location} />
        )}
      {!hasGlobalPermission(Manage, SafetyProgramAnswers) &&
        hasOrganizationPermission(Manage, SafetyProgramAnswers) &&
        hasVerificationSubscription && (
          <SafetyProgramsContainer navExpanded={navExpanded} organizationId={organization.id} location={location} />
        )}
      {isVisibleForNonChasOrganization && <TrainingSection organization={organization} />}
      {(organization.features.includes(Subcontractor) || organization.features.includes(PrimeContractor)) &&
        hasOrganizationPermission(Read, Subcontractors) && (
          <SubcontractorManagementContainer organization={organization} />
        )}
    </React.Fragment>
  );
};
